import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { formatGameTime, hasGameStarted } from '../utils/formatters';
import { LEAGUES, ROUTES } from '../utils/constants';
import './GameCard.css';

const SOCCER_SLUGS = ['epl', 'mls', 'laliga', 'seriea', 'bundesliga', 'ucl'];

// Parse hex color to {r,g,b}
function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

// Relative luminance (0=black, 1=white)
function luminance({ r, g, b }) {
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

// Lighten a color toward white by a factor (0=no change, 1=white)
function lighten(hex, factor) {
  const { r, g, b } = hexToRgb(hex);
  const lr = Math.round(r + (255 - r) * factor);
  const lg = Math.round(g + (255 - g) * factor);
  const lb = Math.round(b + (255 - b) * factor);
  return `rgb(${lr},${lg},${lb})`;
}

// Hue in degrees from hex
function hue(hex) {
  const { r, g, b } = hexToRgb(hex);
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  if (max === min) return 0;
  const d = max - min;
  let h;
  if (max === rn) h = ((gn - bn) / d + 6) % 6;
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return h * 60;
}

// Ensure color is visible on dark bg — use alternate color if primary is too dark
function adjustColor(hex, altHex) {
  const lum = luminance(hexToRgb(hex));
  if (lum < 0.25) {
    // Primary is too dark — try alternate color
    if (altHex) {
      const altLum = luminance(hexToRgb(altHex));
      if (altLum >= 0.25) return `#${altHex.replace('#', '')}`;
      // Alternate is also dark — lighten the brighter of the two
      const brighterHex = altLum > lum ? altHex : hex;
      const factor = (0.25 - Math.max(altLum, lum)) / 0.25 * 0.55;
      return lighten(brighterHex, Math.min(factor, 0.55));
    }
    // No alternate available — lighten primary
    const factor = (0.25 - lum) / 0.25 * 0.55;
    return lighten(hex, Math.min(factor, 0.55));
  }
  return `#${hex.replace('#', '')}`;
}

// Check if a color is too dark or too gray to be useful as a contrast color
function isDarkOrGray(hex) {
  if (!hex) return true;
  const { r, g, b } = hexToRgb(hex);
  const lum = luminance({ r, g, b });
  if (lum < 0.25) return true;
  // Check if it's gray (low saturation) — all channels close together
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const saturation = max === 0 ? 0 : (max - min) / max;
  return saturation < 0.15;
}

function colorsAreSimilar(hexA, hexB) {
  const hueDiff = Math.abs(hue(hexA) - hue(hexB));
  const hueClose = hueDiff < 30 || hueDiff > 330;
  const lumDiff = Math.abs(luminance(hexToRgb(hexA)) - luminance(hexToRgb(hexB)));
  return hueClose && lumDiff < 0.15;
}

function resolveHex(color) {
  if (color.startsWith('rgb')) return null; // lightened color, no hex available
  return color.startsWith('#') ? color.slice(1) : color;
}

function getBarColors(awayHex, homeHex, awayAltHex, homeAltHex) {
  let away = adjustColor(awayHex, awayAltHex);
  let home = adjustColor(homeHex, homeAltHex);

  const awayR = resolveHex(away) || awayHex;
  const homeR = resolveHex(home) || homeHex;

  if (colorsAreSimilar(awayR, homeR)) {
    // Resolved colors are too similar — try swapping one to an alternate
    if (!isDarkOrGray(awayAltHex) && !colorsAreSimilar(awayAltHex, homeR)) {
      away = `#${awayAltHex.replace('#', '')}`;
    } else if (!isDarkOrGray(homeAltHex) && !colorsAreSimilar(homeAltHex, awayR)) {
      home = `#${homeAltHex.replace('#', '')}`;
    } else {
      // Alts don't help — lighten the dark primary instead of using a conflicting alt
      // Re-resolve: use original primaries with lightening, skip alt swap
      const awayLum = luminance(hexToRgb(awayHex));
      const homeLum = luminance(hexToRgb(homeHex));
      if (awayLum < homeLum) {
        away = lighten(awayHex, 0.5);
      } else {
        home = lighten(homeHex, 0.5);
      }
    }
  }

  return { away, home };
}

export default function GameCard({ game, activePick, disabled }) {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();

  const isPickedGame = activePick?.gameId === game.gameId;
  const pickedSide = isPickedGame ? activePick.pick : null;
  const gameStarted = game.status === 'in_progress' || game.status === 'final' || hasGameStarted(game.startTime);
  const cantPick = disabled || gameStarted;
  const isSoccer = SOCCER_SLUGS.includes(game.leagueSlug);

  const hasOdds = game.homeWinPct != null && game.awayWinPct != null;
  const rawAway = game.awayTeam?.color || 'e94560';
  const rawHome = game.homeTeam?.color || '4ecdc4';
  const altAway = game.awayTeam?.alternateColor || null;
  const altHome = game.homeTeam?.alternateColor || null;
  const barColors = getBarColors(rawAway, rawHome, altAway, altHome);
  const awayColor = barColors.away;
  const homeColor = barColors.home;

  const handlePick = (side) => {
    if (cantPick) return;

    // Redirect to login if not authenticated
    if (!isAuthenticated) {
      navigate(ROUTES.LOGIN, { state: { from: { pathname: ROUTES.GAMES } } });
      return;
    }

    navigate(`/games/${game.gameId}/pick`, { state: { game, side } });
  };

  const leagueInfo = LEAGUES[game.leagueSlug] || {};

  return (
    <div className={`game-card ${isPickedGame ? 'game-card--picked' : ''} ${gameStarted ? 'game-card--started' : ''}`}>
      <div className="game-card-header">
        <span className="game-league">
          {leagueInfo.emoji} {game.league}
        </span>
        <span className={`game-time ${game.status === 'in_progress' ? 'game-time--live' : ''}`}>
          {game.status === 'final' ? 'Final' :
           game.status === 'in_progress' ? (
            <><span className="live-dot" />Live</>
           ) :
           formatGameTime(game.startTime)}
        </span>
      </div>

      <div className="game-card-matchup">
        <button
          className={`team-btn ${pickedSide === 'away' ? 'team-btn--picked' : ''}`}
          onClick={() => handlePick('away')}
          disabled={cantPick}
        >
          <span className="team-name">{game.awayTeam.name}</span>
          {gameStarted && game.awayScore != null && (
            <span className="team-score">{game.awayScore}</span>
          )}
        </button>

        {isSoccer ? (
          <button
            className={`draw-btn ${pickedSide === 'draw' ? 'draw-btn--picked' : ''}`}
            onClick={() => handlePick('draw')}
            disabled={cantPick}
          >
            Draw
          </button>
        ) : (
          <span className="matchup-vs">@</span>
        )}

        <button
          className={`team-btn ${pickedSide === 'home' ? 'team-btn--picked' : ''}`}
          onClick={() => handlePick('home')}
          disabled={cantPick}
        >
          <span className="team-name">{game.homeTeam.name}</span>
          {gameStarted && game.homeScore != null && (
            <span className="team-score">{game.homeScore}</span>
          )}
        </button>
      </div>

      {hasOdds && !gameStarted && (
        <div className="game-card-win-prob">
          <div className="win-prob-label">Win Probability</div>
          <div className="win-prob-bar">
            <div
              className="win-prob-bar-segment"
              style={{ width: `${game.awayWinPct}%`, background: awayColor, borderRadius: '3px 0 0 3px' }}
            />
            <div
              className="win-prob-bar-segment"
              style={{ width: `${game.homeWinPct}%`, background: homeColor, borderRadius: '0 3px 3px 0' }}
            />
          </div>
          <div className="win-prob-pcts">
            <span className="win-prob-pct" style={{ color: awayColor }}>{game.awayWinPct}%</span>
            <span className="win-prob-pct" style={{ color: homeColor }}>{game.homeWinPct}%</span>
          </div>
        </div>
      )}

      {game.spread != null && !gameStarted && (
        <div className="game-card-spread">
          {game.spread > 0 ? `${game.awayTeam.abbreviation} -${game.spread}` :
           game.spread < 0 ? `${game.homeTeam.abbreviation} ${game.spread}` :
           'Pick\'em'}
          {game.overUnder != null && ` · O/U ${game.overUnder}`}
        </div>
      )}

      {isPickedGame && (
        <div className="game-card-pick-badge">
          Your pick: {activePick.pickTeamName}
        </div>
      )}
    </div>
  );
}
